import { Link } from 'react-router-dom';
import LegalClause from './LegalClause';
import { BUSINESS } from '../../constants/businessDetails';

/**
 * Shipping & Delivery policy body content — rendered by the /shipping-policy
 * page and by the homepage's Trust Center tab. Required by Razorpay of every
 * merchant site. iAdvocate is a digital subscription: nothing physical is
 * shipped, so this says how and when the service is delivered instead.
 *
 * `onOpenRefund`: when set (the homepage tab), the cross-reference switches
 * the open document instead of leaving the page.
 */
/** The page's overline and title, shared by /shipping-policy and the homepage tab. */
export const SHIPPING_POLICY_HEADING = {
  overline: 'Shipping & Delivery',
  title: 'Shipping and Delivery Policy',
} as const;

interface ShippingPolicyContentProps {
  onOpenRefund?: () => void;
}

export default function ShippingPolicyContent({ onOpenRefund }: ShippingPolicyContentProps) {
  const refundLink = onOpenRefund ? (
    <button type="button" onClick={onOpenRefund} className="underline">
      Cancellation and Refund Policy
    </button>
  ) : (
    <Link to="/refund-policy" className="underline">Cancellation and Refund Policy</Link>
  );

  return (
    <>
      <p className="text-sm text-text-secondary mb-8">
        {BUSINESS.brand} is operated by <strong>{BUSINESS.legalName}</strong>. Last updated 3 November 2026.
      </p>

      <LegalClause id="d1" n="1" title="Nothing is shipped">
        <p>{BUSINESS.brand} is an online software service used in your web browser. No physical goods are sold or shipped, so there are no shipping charges, delivery addresses or courier timelines.</p>
      </LegalClause>

      <LegalClause id="d2" n="2" title="How the service is delivered">
        <p>2.1 Your plan is activated on your {BUSINESS.brand} account as soon as the payment is confirmed — usually within a few minutes.</p>
        <p>2.2 The GST tax invoice is emailed to the address on your account and can also be downloaded from your Profile page.</p>
        <p>2.3 If your plan is not active within 24 hours of a successful payment, write to <strong>{BUSINESS.accountsEmail}</strong> with the payment reference and we will activate it or refund the payment.</p>
      </LegalClause>

      <LegalClause id="d3" n="3" title="Exchanges">
        <p>A digital subscription cannot be exchanged. To move to a different plan, upgrade from your Profile page; cancellations and refunds are covered by our{' '}
          {refundLink}.
        </p>
      </LegalClause>
    </>
  );
}
